import React from 'react';
import { ALL_SCALES, scaleFieldFor, leaningFor } from './constants';

const GAP_THRESHOLD = 3;
const ALIGN_THRESHOLD = 1;

// Builds the pairs both people have answered, ordered by how far apart they are.
export const buildComparison = (theirProfile, myProfile) => {
  const pairs = ALL_SCALES
    .map(s => {
      const field = scaleFieldFor(s.id);
      const theirs = theirProfile?.[field];
      const mine = myProfile?.[field];
      if (theirs == null || mine == null) return null;
      return { id: s.id, theirs, mine, gap: Math.abs(theirs - mine) };
    })
    .filter(Boolean);

  return {
    answered: pairs.length,
    gaps: pairs.filter(p => p.gap >= GAP_THRESHOLD).sort((a, b) => b.gap - a.gap).slice(0, 3),
    aligned: pairs.filter(p => p.gap <= ALIGN_THRESHOLD).sort((a, b) => a.gap - b.gap).slice(0, 3),
  };
};

const Line = ({ pair, name, tone }) => {
  const theirLean = leaningFor(pair.id, pair.theirs);
  const myLean = leaningFor(pair.id, pair.mine);
  return (
    <li className="text-sm text-charcoal/70 leading-relaxed" data-testid={`compare-line-${pair.id}`}>
      <span className={tone === 'gap' ? 'text-orange-primary' : 'text-green-700'}>•</span>{' '}
      {tone === 'gap' ? (
        <>
          <span className="text-charcoal">{name}</span> leans towards {theirLean}, you lean towards {myLean}.
        </>
      ) : theirLean === 'a bit of both' && myLean === 'a bit of both' ? (
        <>
          You and <span className="text-charcoal">{name}</span> both sit in the middle here.
        </>
      ) : (
        <>
          You and <span className="text-charcoal">{name}</span> both lean towards {theirLean === 'a bit of both' ? myLean : theirLean}.
        </>
      )}
    </li>
  );
};

const ComparisonSummary = ({ theirProfile, myProfile, name }) => {
  const { answered, gaps, aligned } = buildComparison(theirProfile, myProfile);
  if (answered === 0) return null;

  return (
    <div
      className="rounded-xl border border-charcoal/10 bg-white/50 p-4 mb-5"
      data-testid="comparison-summary"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-xs uppercase tracking-wider text-charcoal/50">
          You &amp; {name}
        </p>
        <p className="text-xs text-charcoal/40">{answered} of 12 compared</p>
      </div>

      {gaps.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-charcoal/50 mb-1.5">Worth talking about</p>
          <ul className="space-y-1.5">
            {gaps.map(p => <Line key={p.id} pair={p} name={name} tone="gap" />)}
          </ul>
        </div>
      )}

      {aligned.length > 0 && (
        <div>
          <p className="text-xs text-charcoal/50 mb-1.5">Naturally in sync</p>
          <ul className="space-y-1.5">
            {aligned.map(p => <Line key={p.id} pair={p} name={name} tone="align" />)}
          </ul>
        </div>
      )}

      {gaps.length === 0 && aligned.length === 0 && (
        <p className="text-sm text-charcoal/60">
          Your answers sit close together across the board — no strong pulls either way.
        </p>
      )}
    </div>
  );
};

export default ComparisonSummary;
